import { Box, Popover, Typography, styled } from "@mui/material";
import { KeyboardArrowDown, FilterAlt } from "@mui/icons-material";
import { useState } from "react";

export type FilterOption = {
  label: string;
  value: string | null; // null = "All" / no filter
};

export type FilterGroup = {
  key: string;
  label: string;
  options: FilterOption[];
  defaultValue?: string | null;
};

type FilterPanelProps = {
  groups: FilterGroup[];
  onChange: (values: Record<string, string | null>) => void;
};

export const FilterPanel = ({ groups, onChange }: FilterPanelProps) => {
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const [values, setValues] = useState<Record<string, string | null>>(() =>
    Object.fromEntries(groups.map((g) => [g.key, g.defaultValue ?? null]))
  );

  const open = Boolean(anchorEl);
  const activeCount = Object.values(values).filter((v) => v !== null).length;

  const handleSelect = (key: string, value: string | null) => {
    const newValues = { ...values, [key]: value };
    setValues(newValues);
    onChange(newValues);
  };

  return (
    <>
      <TriggerButton onClick={(e) => setAnchorEl(e.currentTarget)}>
        <FilterAlt sx={{ fontSize: 16, color: "#fff" }} />
        {activeCount > 0 && <ActiveBadge>{activeCount}</ActiveBadge>}
        <KeyboardArrowDown sx={{ fontSize: 18, color: "#fff" }} />
      </TriggerButton>

      <Popover
        open={open}
        anchorEl={anchorEl}
        onClose={() => setAnchorEl(null)}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        transformOrigin={{ vertical: "top", horizontal: "right" }}
        PaperProps={{
          sx: { mt: "8px", borderRadius: "16px", bgcolor: "transparent", boxShadow: "none" },
        }}
      >
        <Panel>
          {groups.map((group) => (
            <Box key={group.key} display="flex" flexDirection="column" gap="10px">
              <GroupLabel>{group.label}</GroupLabel>
              <OptionsRow>
                {group.options.map((option) => (
                  <OptionButton
                    key={option.label}
                    active={values[group.key] === option.value}
                    onClick={() => handleSelect(group.key, option.value)}
                  >
                    {option.label}
                  </OptionButton>
                ))}
              </OptionsRow>
            </Box>
          ))}
        </Panel>
      </Popover>
    </>
  );
};

const TriggerButton = styled(Box)({
  display: "inline-flex",
  alignItems: "center",
  gap: "6px",
  background: "#000",
  borderRadius: "40px",
  padding: "10px 18px",
  cursor: "pointer",
  userSelect: "none",
  "&:hover": { opacity: 0.8 },
});

const ActiveBadge = styled(Typography)({
  color: "#fff",
  fontSize: "14px",
  fontWeight: 700,
  lineHeight: 1,
});

const Panel = styled(Box)({
  display: "flex",
  flexDirection: "column",
  gap: "20px",
  padding: "20px",
  minWidth: "380px",
  borderRadius: "16px",
  background: "#1a1a1a",
  boxShadow: "0px 8px 32px rgba(0,0,0,0.5)",
});

const GroupLabel = styled(Typography)({
  color: "#9b9b9b",
  fontSize: "12px",
  fontWeight: 400,
  textTransform: "uppercase",
  letterSpacing: "0.5px",
});

const OptionsRow = styled(Box)({
  display: "flex",
  gap: "8px",
  flexWrap: "wrap",
});

const OptionButton = styled(Box)<{ active: boolean }>(({ active }) => ({
  cursor: "pointer",
  padding: "8px 20px",
  borderRadius: "47px",
  background: active ? "#FFFF00" : "transparent",
  border: "1px solid",
  borderColor: active ? "#FFFF00" : "#525252",
  color: active ? "#000" : "#fff",
  fontSize: "13px",
  fontWeight: active ? 700 : 400,
  userSelect: "none",
  transition: "all 0.15s ease",
  "&:hover": {
    borderColor: active ? "#FFFF00" : "#888",
    opacity: 0.9,
  },
}));
